// PR-26: Cards tab — flat reverse-chronological list with search + kind filter.
// PR-33: adds a frame lens. When the user picks a frame from the lens chip, the
// view switches from flat to a recursive sections-and-subsections tree (mirror
// of CardsPanel). When the lens is "All cards", PR-26's flat list still
// renders. Frame MECE gaps surface to the parent (MobileShell) so PR-34 can
// render them in the Gaps tab's "FRAME GAPS" section.
//
// What we deliberately don't port from desktop CardsPanel:
//   - new-frame creation, document generation, hidden-cards toggle, deleted
//     view (M1 decision: heavy ops live on desktop). They land in PR-35 if
//     at all.
//   - drag-and-drop card placement (mobile UX is tap-first).

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  PATH_EXCLUDED,
  PATH_UNASSIGNED,
  type Card,
  type FrameDef,
  type FrameNode,
  type FrameView,
} from '../../api'
import { specForKind } from '../../kinds'
import { KIND_GROUP_ORDER, type FrameGapInfo } from '../CardsPanel'

interface MobileCardsProps {
  projectId: string | null
  cards: Card[]
  cardsRevision: number
  onPeek: (cardId: string) => void
  onRefresh: () => void | Promise<void>
  /** PR-34 wires this into the Gaps tab so users can see all empty
   *  mece_required nodes from the active frame. Empty array when the
   *  lens is flat / there's no active frame / there are no gaps. */
  onFrameGapsChange?: (gaps: FrameGapInfo[]) => void
}

type FilterId = 'all' | 'decisions' | 'facts' | 'questions' | 'built'

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'decisions', label: 'Decisions' },
  { id: 'facts', label: 'Facts' },
  { id: 'questions', label: 'Questions' },
  { id: 'built', label: 'Built' },
]

function matchesFilter(c: Card, f: FilterId): boolean {
  switch (f) {
    case 'all':
      return true
    case 'decisions':
      return c.kind === 'decision'
    case 'questions':
      return c.kind === 'question'
    case 'facts':
      return c.kind === 'fact' || c.kind === 'statement' || c.kind === 'observation'
    case 'built':
      return c.implementation_status === 'built'
  }
}

function matchesQuery(c: Card, q: string): boolean {
  if (!q) return true
  const needle = q.toLowerCase()
  return (
    (c.title ?? '').toLowerCase().includes(needle) ||
    c.body.toLowerCase().includes(needle) ||
    c.card_id.toLowerCase().includes(needle)
  )
}

function deriveTitle(c: Card): string {
  if (c.title?.trim()) return c.title.trim()
  const firstLine = c.body
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0)
  if (!firstLine) return '(untitled)'
  return firstLine.length > 80 ? firstLine.slice(0, 80).trimEnd() + '…' : firstLine
}

interface SectionTree {
  topNode: FrameNode
  cardsAtSelf: Array<{ card: Card; overridden: boolean }>
  subSections: Array<{
    node: FrameNode
    cards: Array<{ card: Card; overridden: boolean }>
  }>
}

function buildSections(view: FrameView | null, allCards: Card[]): SectionTree[] {
  if (!view) return []
  const cardById = new Map<string, Card>()
  for (const c of allCards) cardById.set(c.card_id, c)

  const bucket = new Map<string, Array<{ card: Card; overridden: boolean }>>()
  for (const [cardId, p] of Object.entries(view.placements)) {
    if (p.node_path === PATH_EXCLUDED) continue
    if (p.node_path === PATH_UNASSIGNED) continue
    const card = cardById.get(cardId)
    if (!card) continue
    if (!bucket.has(p.node_path)) bucket.set(p.node_path, [])
    bucket.get(p.node_path)!.push({ card, overridden: p.override_by_user })
  }

  const tops = view.frame.topology.filter((n) => !n.path.includes('/'))
  return tops.map((top) => {
    const childNodes = view.frame.topology.filter((n) =>
      n.path.startsWith(top.path + '/'),
    )
    const cardsAtSelf = bucket.get(top.path) ?? []
    const subSections = childNodes.map((child) => ({
      node: child,
      cards: bucket.get(child.path) ?? [],
    }))
    return { topNode: top, cardsAtSelf, subSections }
  })
}

export function MobileCards({
  projectId,
  cards,
  cardsRevision,
  onPeek,
  onRefresh,
  onFrameGapsChange,
}: MobileCardsProps) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<FilterId>('all')
  const [refreshing, setRefreshing] = useState(false)

  // Lens state mirrors desktop CardsPanel: null = "Flat / All cards",
  // string = a frame id. Each project can pick its own; we don't persist
  // across sessions for PR-33 (PR-35+ could).
  const [frames, setFrames] = useState<FrameDef[]>([])
  const [activeFrameId, setActiveFrameId] = useState<string | null>(null)
  const [frameView, setFrameView] = useState<FrameView | null>(null)
  const [frameLoading, setFrameLoading] = useState(false)
  const [frameError, setFrameError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Sub-axis for the no-frame view — mirrors desktop CardsPanel (4adffc5).
  // 'none' = newest-first flat list (the default); 'kind' = bucket by
  // epistemic kind with section headers.
  const [groupBy, setGroupBy] = useState<'none' | 'kind'>('none')
  // Lens "Show archived" toggle. Off by default — the parent's `cards`
  // prop is already the include_archived=false list, so flipping this on
  // triggers a local re-fetch with include_archived=true and renders that
  // copy instead. Independent of the active frame / axis.
  const [showArchived, setShowArchived] = useState(false)
  const [archivedCards, setArchivedCards] = useState<Card[] | null>(null)

  // Reload frames on project change. Skip auto-select — mobile defaults
  // to flat view; the user opts into a frame explicitly.
  useEffect(() => {
    if (!projectId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFrames([])
      setActiveFrameId(null)
      setFrameView(null)
      return
    }
    let cancelled = false
    api.listFrames(projectId)
      .then((fs) => {
        if (cancelled) return
        setFrames(fs.sort((a, b) => a.priority - b.priority))
      })
      .catch(() => {
        // Frames endpoint can 4xx pre-migration; fall back to empty.
        if (!cancelled) setFrames([])
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  // Fetch archived cards on demand. Triggered by the lens "Show archived"
  // toggle — without this side-fetch the parent's `cards` prop only
  // contains live cards. We refetch on cardsRevision so newly-archived
  // cards appear without manual refresh.
  useEffect(() => {
    if (!projectId || !showArchived) {
      setArchivedCards(null)
      return
    }
    let cancelled = false
    api.listCards(projectId, { includeArchived: true })
      .then((all) => {
        if (!cancelled) setArchivedCards(all)
      })
      .catch(() => {
        if (!cancelled) setArchivedCards(null)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, showArchived, cardsRevision])

  // The cards array actually rendered. When showArchived is on and the
  // archived-side fetch has landed, prefer that (it includes both live
  // and archived); otherwise the parent's filtered list.
  const effectiveCards = showArchived && archivedCards ? archivedCards : cards

  // SWR cache for frame views (mirrors desktop CardsPanel). Avoids the
  // 100-300ms blank gap on every cardsRevision-driven refetch — paint
  // the previous layout, swap to fresh on arrival.
  const frameViewCacheRef = useRef<Record<string, FrameView>>({})

  // Load frame view on lens change. Re-run on cardsRevision so writes/
  // promotes/edits show up under the right node without a manual refresh.
  useEffect(() => {
    if (!projectId || !activeFrameId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFrameView(null)
      return
    }
    const key = `${projectId}|${activeFrameId}`
    const cached = frameViewCacheRef.current[key]
    let cancelled = false
    if (cached) {
      setFrameView(cached)
      setFrameError(null)
    } else {
      setFrameLoading(true)
      setFrameError(null)
    }
    api.getFrame(projectId, activeFrameId)
      .then((v) => {
        if (cancelled) return
        frameViewCacheRef.current[key] = v
        setFrameView(v)
      })
      .catch((e) => {
        if (cancelled) return
        setFrameError(`load frame: ${(e as Error).message}`)
        setFrameView(null)
      })
      .finally(() => {
        if (!cancelled) setFrameLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, activeFrameId, cardsRevision])

  // Surface MECE gaps from the active frame to the parent. Empty when no
  // frame is active (matches desktop behavior).
  useEffect(() => {
    if (!onFrameGapsChange) return
    if (!frameView || !activeFrameId) {
      onFrameGapsChange([])
      return
    }
    const nodeByPath = new Map(frameView.frame.topology.map((n) => [n.path, n]))
    const gaps: FrameGapInfo[] = []
    for (const path of frameView.mece_gaps) {
      const node = nodeByPath.get(path)
      if (node) {
        gaps.push({
          frameId: activeFrameId,
          frameName: frameView.frame.name,
          node,
        })
      }
    }
    onFrameGapsChange(gaps)
  }, [frameView, activeFrameId, onFrameGapsChange])

  const activeFrame = useMemo(
    () => frames.find((f) => f.id === activeFrameId) ?? null,
    [frames, activeFrameId],
  )

  const sections = useMemo(
    () => buildSections(frameView, effectiveCards),
    [frameView, effectiveCards],
  )

  // Apply search to the flat list; in frame view, the search filters
  // each section's contents. Match the desktop pattern: filter chips
  // only relevant in flat view (frame placements are already a structure).
  const flatVisible = useMemo(() => {
    const out = effectiveCards.filter(
      (c) => matchesFilter(c, filter) && matchesQuery(c, query),
    )
    return out.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
  }, [effectiveCards, filter, query])

  const doRefresh = async () => {
    setRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setRefreshing(false)
    }
  }

  // Chip label reflects the *current view* — "All cards" was confusing
  // because flat-time and flat-by-kind are now both surfaced as first-class
  // options in the picker. Mirrors desktop CardsPanel chipLabel.
  const lensLabel = activeFrame
    ? activeFrame.name
    : groupBy === 'kind'
      ? 'Status'
      : 'Time'

  return (
    <section className="mobile-cards">
      <div className="mobile-cards__head">
        <button
          type="button"
          className="mobile-cards__lens"
          onClick={() => setPickerOpen(true)}
          aria-label="Switch lens"
        >
          <span className="mobile-cards__lens-icon" aria-hidden>◆</span>
          <span className="mobile-cards__lens-label">{lensLabel}</span>
          <span className="mobile-cards__lens-chevron" aria-hidden>▾</span>
        </button>
        <div className="mobile-cards__searchrow">
          <input
            type="search"
            className="mobile-cards__search"
            placeholder={
              activeFrame ? `Search in ${activeFrame.name}…` : 'Search cards…'
            }
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            className="mobile-cards__refresh"
            onClick={doRefresh}
            disabled={refreshing}
            aria-label="Refresh"
          >
            {refreshing ? '…' : '↻'}
          </button>
        </div>
        {/* Kind-filter chips only make sense in the flat-time view. When
            grouping by kind, the section headers themselves carry the
            kind grouping; chips would be redundant. Frame views don't
            use chips at all (placement structure replaces them). */}
        {!activeFrame && groupBy === 'none' && (
          <div className="mobile-cards__chips" role="tablist">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className="mobile-cards__chip"
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {activeFrame ? (
        <FrameSectionsView
          frame={activeFrame}
          sections={sections}
          query={query}
          loading={frameLoading}
          error={frameError}
          onPeek={onPeek}
        />
      ) : flatVisible.length === 0 ? (
        <div className="mobile-cards__empty">
          {query.trim() ? 'No matches.' : 'No cards yet — type in Chat to start.'}
        </div>
      ) : groupBy === 'kind' ? (
        <KindGroupedList cards={flatVisible} onPeek={onPeek} />
      ) : (
        <ul className="mobile-cards__list">
          {flatVisible.map((c) => (
            <li key={c.card_id}>
              <CardListItem card={c} onPeek={onPeek} />
            </li>
          ))}
        </ul>
      )}

      {pickerOpen && (
        <LensPicker
          frames={frames}
          activeFrameId={activeFrameId}
          groupBy={groupBy}
          showArchived={showArchived}
          onPickFrame={(id) => {
            setActiveFrameId(id)
            setPickerOpen(false)
          }}
          onPickGroup={(g) => {
            setActiveFrameId(null)
            setGroupBy(g)
            setPickerOpen(false)
          }}
          onToggleShowArchived={() => {
            setShowArchived((s) => !s)
            setPickerOpen(false)
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </section>
  )
}

// ── Frame view ─────────────────────────────────────────────────────
function FrameSectionsView({
  frame,
  sections,
  query,
  loading,
  error,
  onPeek,
}: {
  frame: FrameDef
  sections: SectionTree[]
  query: string
  loading: boolean
  error: string | null
  onPeek: (cardId: string) => void
}) {
  if (loading && sections.length === 0) {
    return <div className="mobile-cards__empty mono">loading frame…</div>
  }
  if (error) {
    return <div className="mobile-cards__empty mobile-cards__empty--err">{error}</div>
  }
  if (sections.length === 0) {
    return (
      <div className="mobile-cards__empty">
        {frame.name} has no top-level nodes yet.
      </div>
    )
  }
  // Filter cards by query within each section/subsection. A section with
  // zero matching cards is still rendered (so the user sees the empty
  // mece_required gap), but not its subsections that have nothing.
  return (
    <div className="mobile-cards__sections">
      {sections.map((sec) => (
        <SectionBlock
          key={sec.topNode.path}
          sec={sec}
          query={query}
          onPeek={onPeek}
        />
      ))}
    </div>
  )
}

function SectionBlock({
  sec,
  query,
  onPeek,
}: {
  sec: SectionTree
  query: string
  onPeek: (cardId: string) => void
}) {
  const filteredAtSelf = sec.cardsAtSelf.filter(({ card }) => matchesQuery(card, query))
  const cardCount = filteredAtSelf.length
  const subFiltered = sec.subSections
    .map((sub) => ({
      ...sub,
      cards: sub.cards.filter(({ card }) => matchesQuery(card, query)),
    }))
    .filter((sub) => sub.cards.length > 0 || sub.node.mece_required)
  const totalInSec = cardCount + subFiltered.reduce((n, s) => n + s.cards.length, 0)
  const isGap = sec.topNode.mece_required && totalInSec === 0

  return (
    <div className="mobile-cards__section">
      <header
        className={
          'mobile-cards__section-header' +
          (isGap ? ' mobile-cards__section-header--gap' : '')
        }
      >
        <span className="mobile-cards__section-label">{sec.topNode.label}</span>
        {isGap && (
          <span className="mobile-cards__section-gap-tag mono">EMPTY · gap</span>
        )}
        {!isGap && (
          <span className="mobile-cards__section-count mono">{totalInSec}</span>
        )}
      </header>
      {sec.topNode.description && (
        <div className="mobile-cards__section-desc">{sec.topNode.description}</div>
      )}
      {filteredAtSelf.length > 0 && (
        <ul className="mobile-cards__list">
          {filteredAtSelf.map(({ card }) => (
            <li key={card.card_id}>
              <CardListItem card={card} onPeek={onPeek} />
            </li>
          ))}
        </ul>
      )}
      {subFiltered.map((sub) => (
        <div key={sub.node.path} className="mobile-cards__subsection">
          <header
            className={
              'mobile-cards__subsection-header' +
              (sub.node.mece_required && sub.cards.length === 0
                ? ' mobile-cards__subsection-header--gap'
                : '')
            }
          >
            <span className="mobile-cards__subsection-label">{sub.node.label}</span>
            {sub.cards.length > 0 ? (
              <span className="mobile-cards__section-count mono">{sub.cards.length}</span>
            ) : sub.node.mece_required ? (
              <span className="mobile-cards__section-gap-tag mono">empty</span>
            ) : null}
          </header>
          {sub.cards.length > 0 && (
            <ul className="mobile-cards__list">
              {sub.cards.map(({ card }) => (
                <li key={card.card_id}>
                  <CardListItem card={card} onPeek={onPeek} />
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Lens picker bottom sheet ───────────────────────────────────────
// Mirrors desktop LensDropdown (4adffc5):
//   Default views: Time, Status (no-frame, axis-driven, pure SQL)
//   Frames        : per-project lenses
//   Toggle        : Show archived (global, applies to any view)
function LensPicker({
  frames,
  activeFrameId,
  groupBy,
  showArchived,
  onPickFrame,
  onPickGroup,
  onToggleShowArchived,
  onClose,
}: {
  frames: FrameDef[]
  activeFrameId: string | null
  groupBy: 'none' | 'kind'
  showArchived: boolean
  onPickFrame: (id: string) => void
  onPickGroup: (g: 'none' | 'kind') => void
  onToggleShowArchived: () => void
  onClose: () => void
}) {
  const noFrameTime = activeFrameId === null && groupBy === 'none'
  const noFrameStatus = activeFrameId === null && groupBy === 'kind'
  return (
    <div className="mobile-action-sheet-backdrop" onClick={onClose}>
      <div
        className="mobile-action-sheet"
        onClick={(e) => e.stopPropagation()}
        role="menu"
      >
        <div className="mobile-action-sheet__title">View by</div>

        <button
          type="button"
          className="mobile-action-sheet__row"
          aria-current={noFrameTime}
          onClick={() => onPickGroup('none')}
        >
          ◆ Time
          <span className="mobile-action-sheet__row-sub">newest first · default</span>
        </button>
        <button
          type="button"
          className="mobile-action-sheet__row"
          aria-current={noFrameStatus}
          onClick={() => onPickGroup('kind')}
        >
          ◆ Status
          <span className="mobile-action-sheet__row-sub">grouped by kind</span>
        </button>

        {frames.length > 0 && <div className="mobile-action-sheet__rule" />}
        {frames.map((f) => (
          <button
            key={f.id}
            type="button"
            className="mobile-action-sheet__row"
            aria-current={activeFrameId === f.id}
            onClick={() => onPickFrame(f.id)}
          >
            ◇ {f.name}
          </button>
        ))}

        <div className="mobile-action-sheet__rule" />
        <button
          type="button"
          className="mobile-action-sheet__row"
          aria-pressed={showArchived}
          onClick={onToggleShowArchived}
          title="Soft-hidden cards — kept in DB so wikilinks resolve, hidden by default"
        >
          {showArchived ? '☑' : '☐'} Show archived
        </button>

        <button
          type="button"
          className="mobile-action-sheet__row mobile-action-sheet__row--cancel"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── Kind-grouped list ──────────────────────────────────────────────
// Mirror of desktop FlatList's groupBy='kind' branch (CardsPanel.tsx).
// Buckets cards by epistemic kind, ordered by KIND_GROUP_ORDER, with
// any unknown kinds tail-appended in first-seen order so legacy data
// never silently drops.
function KindGroupedList({
  cards,
  onPeek,
}: {
  cards: Card[]
  onPeek: (cardId: string) => void
}) {
  const buckets = new Map<string, Card[]>()
  for (const c of cards) {
    const arr = buckets.get(c.kind) ?? []
    arr.push(c)
    buckets.set(c.kind, arr)
  }
  const orderedKinds: string[] = [
    ...KIND_GROUP_ORDER.filter((k) => buckets.has(k)),
    ...[...buckets.keys()].filter((k) => !KIND_GROUP_ORDER.includes(k)),
  ]
  return (
    <div className="mobile-cards__sections">
      {orderedKinds.map((kind) => {
        const items = buckets.get(kind) ?? []
        const spec = specForKind(kind)
        return (
          <div key={kind} className="mobile-cards__section">
            <header className="mobile-cards__section-header">
              <span
                className="mobile-cards__section-label"
                style={{ color: spec.color }}
              >
                {spec.label}
              </span>
              <span className="mobile-cards__section-count mono">{items.length}</span>
            </header>
            <ul className="mobile-cards__list">
              {items.map((c) => (
                <li key={c.card_id}>
                  <CardListItem card={c} onPeek={onPeek} />
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

// ── Card list item (shared between flat + frame views) ─────────────
function CardListItem({
  card,
  onPeek,
}: {
  card: Card
  onPeek: (cardId: string) => void
}) {
  return (
    <button
      type="button"
      className="mobile-cards__row"
      onClick={() => onPeek(card.card_id)}
    >
      <div className="mobile-cards__rowhead">
        <KindPill kind={card.kind} />
        {card.stale && (
          <span className="mobile-cards__stale-tag mono">STALE</span>
        )}
        {card.implementation_status === 'built' && (
          <span className="mobile-cards__built">built</span>
        )}
      </div>
      <h3 className="mobile-cards__title">{deriveTitle(card)}</h3>
      {card.body.trim() && card.body.trim() !== card.title?.trim() && (
        <p className="mobile-cards__preview">{card.body}</p>
      )}
    </button>
  )
}

/** Mobile kind pill — same color tokens as the desktop CardRow but laid
 *  out for thumb-scroll density. */
export function KindPill({ kind }: { kind: string }) {
  const spec = specForKind(kind)
  return (
    <span
      className="mobile-kind-pill"
      style={{
        color: spec.color,
        background: spec.bg,
        borderColor: spec.rule === 'var(--ink-0)' ? 'var(--rule-s)' : spec.rule,
      }}
    >
      <span aria-hidden>{spec.glyph}</span>
      {spec.label}
    </span>
  )
}
